// Supply depots render as two instanced meshes per depot (base plate + crate)
// tinted by owner. Tier (0..4) scales crate height so upgraded depots read.

import { useEffect, useRef } from 'react';
import type * as THREE from 'three';
import {
  type InfraEntry,
  _color,
  _dummy,
  depotBaseGeo,
  depotCrateGeo,
  depotMat,
  depotTint,
} from './InfrastructureMaterials.js';
import { RenderLayer } from './RenderLayer.js';
import { tileElevationAt } from './elevation.js';

interface DepotInstancedProps {
  readonly entries: ReadonlyArray<InfraEntry>;
  readonly biomeMap: Map<string, string>;
}

export function DepotInstanced({ entries, biomeMap }: DepotInstancedProps) {
  const baseRef = useRef<THREE.InstancedMesh>(null);
  const crateRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    const base = baseRef.current;
    const crate = crateRef.current;
    if (!base || !crate || entries.length === 0) return;

    const dummy = _dummy;
    const color = _color;
    entries.forEach(({ tile }, idx) => {
      const [x, , z] = tile.transform.position;
      const props = tile.props as Record<string, unknown> | undefined;
      const q = (props?.q as number) ?? 0;
      const r = (props?.r as number) ?? 0;
      const elev = tileElevationAt(q, r, biomeMap);
      const ownerProp = props?.depotOwner as number | undefined;
      const tier = (props?.depotTier as number | undefined) ?? 1;
      const tint = depotTint(ownerProp);

      // Base plate — flat on the ground.
      dummy.position.set(x, elev + 0.04, z);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      base.setMatrixAt(idx, dummy.matrix);
      color.copy(tint).multiplyScalar(0.6);
      base.setColorAt(idx, color);

      // Crate — sits on the base, scales with tier (1×..2× height).
      const heightScale = 0.8 + tier * 0.3;
      dummy.position.set(x, elev + 0.06, z);
      dummy.scale.set(1, heightScale, 1);
      dummy.updateMatrix();
      crate.setMatrixAt(idx, dummy.matrix);
      color.copy(tint);
      crate.setColorAt(idx, color);
    });

    base.instanceMatrix.needsUpdate = true;
    crate.instanceMatrix.needsUpdate = true;
    if (base.instanceColor) base.instanceColor.needsUpdate = true;
    if (crate.instanceColor) crate.instanceColor.needsUpdate = true;
    base.count = entries.length;
    crate.count = entries.length;
  }, [entries, biomeMap]);

  return (
    <>
      <instancedMesh
        ref={baseRef}
        args={[depotBaseGeo, depotMat, entries.length]}
        renderOrder={RenderLayer.Infrastructure}
        castShadow
        receiveShadow
      />
      <instancedMesh
        ref={crateRef}
        args={[depotCrateGeo, depotMat, entries.length]}
        renderOrder={RenderLayer.Infrastructure}
        castShadow
        receiveShadow
      />
    </>
  );
}
